import apiController from "@/utils/apiController";

/**
 * Fechamento de mês do subsídio, lado das telas de edição de viagens.
 *
 * Uma viagem de mês fechado está congelada: a API recusa a alteração (409 MES_FECHADO) e as
 * rotinas do monitor a ignoram. Sem aviso na tela o usuário só percebe ao clicar em salvar,
 * então as telas de edição carregam os meses fechados da empresa e usam isto para avisar e
 * desabilitar os campos.
 *
 * O fechamento pode ser de um mês ou de um único dia (tipo_periodo); o período coberto é
 * sempre dt_inicio..dt_fim e o rótulo para aviso é periodo_extenso.
 *
 * O período de uma viagem vem SEMPRE da sua dt_referencia (o dia operacional), nunca do horário
 * de relógio: com hora_inicial_dia_operacao = 03:00, uma viagem que saiu 01/08 às 02:00 tem
 * dt_referencia = 31/07 e pertence a julho.
 */
export default {
  data: () => ({
    fechamentosMesAtivos: [],
    carregandoFechamentosMes: false,
  }),
  computed: {
    /** Há algum mês fechado carregado para o contexto atual. */
    temMesFechado() {
      return this.fechamentosMesAtivos.length > 0;
    },
  },
  methods: {
    /**
     * Carrega os meses fechados (status FECHADO) da empresa. Chamar sempre que a empresa ou
     * o período consultado mudar. Falha em silêncio: sem permissão de leitura a tela apenas
     * não mostra o aviso, e a API continua sendo a barreira de verdade.
     */
    carregaFechamentosMesAtivos(idEmpresa) {
      if (!idEmpresa) {
        this.fechamentosMesAtivos = [];
        return Promise.resolve([]);
      }
      this.carregandoFechamentosMes = true;
      return apiController
        .request(apiController.endpoints.subsidioGetallFechamentosmes, {
          data: { idempresa: idEmpresa, somente_ativos: true },
          silent: true,
        })
        .then((response) => {
          this.fechamentosMesAtivos = Array.isArray(response.data) ? response.data : [];
          this.carregandoFechamentosMes = false;
          return this.fechamentosMesAtivos;
        })
        .catch(() => {
          this.fechamentosMesAtivos = [];
          this.carregandoFechamentosMes = false;
          return [];
        });
    },

    /** 'YYYY-MM-DD' de uma dt_referencia, sem passar por fuso horário. */
    _diaDaDtReferencia(dtReferencia) {
      if (dtReferencia == null) return null;
      const pad = (n) => String(n).padStart(2, "0");
      if (dtReferencia instanceof Date) {
        return `${dtReferencia.getFullYear()}-${pad(dtReferencia.getMonth() + 1)}-${pad(dtReferencia.getDate())}`;
      }
      const texto = String(dtReferencia).trim();
      const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
      const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (br) return `${br[3]}-${br[2]}-${br[1]}`;
      return null;
    },

    /** [inicio, fim] 'YYYY-MM-DD' de um fechamento; antigos sem dt_inicio cobrem o mês. */
    _periodoDoFechamento(f) {
      const mes = String(f.mes_referencia || "").substring(0, 7);
      const inicio = f.dt_inicio ? String(f.dt_inicio).substring(0, 10) : `${mes}-01`;
      const fim = f.dt_fim ? String(f.dt_fim).substring(0, 10) : `${mes}-31`;
      return [inicio, fim];
    },

    /**
     * Fechamento ativo que congela essa dt_referencia, ou null. Se o dia e o mês estiverem
     * fechados, devolve o do mês (é ele que precisa ser reaberto primeiro).
     */
    fechamentoDaDtReferencia(dtReferencia, idEmpresa) {
      const dia = this._diaDaDtReferencia(dtReferencia);
      if (!dia || !this.fechamentosMesAtivos.length) return null;
      const cobrem = this.fechamentosMesAtivos.filter((f) => {
        if (idEmpresa != null && Number(f.id_empresa) !== Number(idEmpresa)) return false;
        const [inicio, fim] = this._periodoDoFechamento(f);
        return dia >= inicio && dia <= fim;
      });
      return cobrem.find((f) => f.tipo_periodo !== "DIA") || cobrem[0] || null;
    },

    /** A viagem está congelada por fechamento de mês. */
    viagemEmMesFechado(item, idEmpresa) {
      if (!item) return false;
      const dt = item.dt_referencia != null ? item.dt_referencia : item.dt_referencia_extenso;
      return !!this.fechamentoDaDtReferencia(dt, idEmpresa);
    },

    /** Texto do aviso de uma viagem congelada, para tooltip. */
    avisoViagemMesFechado(item, idEmpresa) {
      const f = this.fechamentoDaDtReferencia(
        item && (item.dt_referencia != null ? item.dt_referencia : item.dt_referencia_extenso),
        idEmpresa
      );
      if (!f) return "";
      return this.$t("closingMonth.rowBlocked", { mes: f.periodo_extenso || f.mes_referencia_extenso });
    },

    /**
     * Períodos fechados que se sobrepõem ao período consultado: meses em MM/AAAA e dias em
     * DD/MM/AAAA. O período das telas de edição é livre e pode atravessar vários meses.
     */
    mesesFechadosNoIntervalo(dti, dtf, idEmpresa) {
      if (!this.fechamentosMesAtivos.length) return [];
      const ini = this._diaDaDtReferencia(dti);
      const fim = this._diaDaDtReferencia(dtf);
      if (!ini || !fim) return [];
      return this.fechamentosMesAtivos
        .filter((f) => {
          if (idEmpresa != null && Number(f.id_empresa) !== Number(idEmpresa)) return false;
          const [inicio, fimF] = this._periodoDoFechamento(f);
          return inicio <= fim && fimF >= ini;
        })
        .map((f) => f.periodo_extenso || f.mes_referencia_extenso)
        .filter(Boolean);
    },
  },
};
